import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { ArrowRight, Check, Phone, Star } from "lucide-react";

import { business } from "@/data/business";
import { cn } from "@/lib/utils";
import { richText } from "@/lib/richtext";
import { AppImage } from "@/components/site/AppImage";
import { HearthBanner } from "@/components/site/HearthBanner";
import { pictureAlt, type Picture } from "@/lib/images";

export function Section({
  children,
  className,
  tone = "default",
  size = "default",
  id,
}: {
  children: ReactNode;
  className?: string | undefined;
  tone?: "default" | "surface" | "ink" | "brand" | undefined;
  size?: "default" | "compact" | undefined;
  id?: string | undefined;
}) {
  const tones = {
    default: "bg-background text-foreground",
    surface: "bg-surface text-foreground",
    ink: "on-dark bg-ink text-ink-foreground",
    brand: "bg-brand text-brand-foreground",
  } as const;
  return (
    <section
      id={id}
      data-section=""
      data-tone={tone}
      className={cn(
        "px-5",
        size === "compact" ? "py-8 md:py-10" : "py-12 md:py-16",
        tones[tone],
        className,
      )}
    >
      <div className="mx-auto w-full max-w-6xl">{children}</div>
    </section>
  );
}

export function Eyebrow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string | undefined;
}) {
  return <p className={cn("eyebrow text-brand", className)}>{children}</p>;
}

export function SectionHeading({
  eyebrow,
  title,
  intro,
  align = "left",
  invert = false,
}: {
  eyebrow?: string | undefined;
  title: string;
  intro?: string | undefined;
  align?: "left" | "center" | undefined;
  invert?: boolean | undefined;
}) {
  return (
    <div className={cn("max-w-3xl", align === "center" && "mx-auto text-center")}>
      {eyebrow ? (
        <Eyebrow className={invert ? "text-brand-foreground/70" : undefined}>{eyebrow}</Eyebrow>
      ) : null}
      <h2 className="mt-2 text-2xl leading-tight md:text-3xl">{title}</h2>
      {intro ? (
        <p className={cn("mt-3 text-base", invert ? "text-current/80" : "text-muted-foreground")}>
          {intro}
        </p>
      ) : null}
    </div>
  );
}

type ButtonTone = "brand" | "ink" | "outline" | "ghost-light";

const buttonBase =
  "inline-flex items-center justify-center gap-2 rounded-sm px-6 py-3.5 font-display text-sm font-bold uppercase tracking-widest transition-colors";

const buttonTones: Record<ButtonTone, string> = {
  brand: "bg-brand text-brand-foreground hover:bg-brand/90",
  ink: "bg-ink text-ink-foreground hover:bg-ink/90",
  outline: "border-2 border-current text-current hover:bg-current/10",
  "ghost-light": "border-2 border-white/25 text-white hover:bg-white/10",
};

export function CtaLink({
  to,
  children,
  tone = "brand",
  className,
}: {
  to: string;
  children: ReactNode;
  tone?: ButtonTone | undefined;
  className?: string | undefined;
}) {
  return (
    <Link to={to as never} className={cn(buttonBase, buttonTones[tone], className)}>
      {children}
    </Link>
  );
}

export function CallButton({
  tone = "ink",
  className,
}: {
  tone?: ButtonTone | undefined;
  className?: string | undefined;
}) {
  return (
    <a href={business.phoneHref} className={cn(buttonBase, buttonTones[tone], className)}>
      <Phone className="size-4" aria-hidden="true" />
      {business.phone}
    </a>
  );
}

export function CtaAnchor({
  href,
  children,
  tone = "brand",
  className,
}: {
  href: string;
  children: ReactNode;
  tone?: ButtonTone | undefined;
  className?: string | undefined;
}) {
  return (
    <a href={href} className={cn(buttonBase, buttonTones[tone], className)}>
      {children}
    </a>
  );
}

export function PageHero({
  title,
  intro,
  image,
  imageAlt,
  children,
}: {
  title: string;
  intro?: string | undefined;
  image?: Picture | undefined;
  imageAlt?: string | undefined;
  children?: ReactNode | undefined;
}) {
  return (
    <section className="on-dark relative overflow-hidden bg-ink text-ink-foreground">
      {image ? (
        <>
          <AppImage
            picture={image}
            alt={imageAlt ?? pictureAlt(image)}
            sizes="100vw"
            priority
            className="absolute inset-0 size-full object-cover opacity-30"
          />
          <div
            className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-ink/60"
            aria-hidden="true"
          />
        </>
      ) : null}
      <div className="absolute inset-y-0 left-0 w-1.5 bg-brand" aria-hidden="true" />
      <div className="relative mx-auto w-full max-w-6xl px-5 py-12 md:py-16">
        <h1 className="max-w-4xl text-3xl leading-[1.08] md:text-5xl">{title}</h1>
        {intro ? (
          <p className="mt-4 max-w-2xl text-base text-white/85 md:text-lg">{intro}</p>
        ) : null}
        {children ? <div className="mt-6 flex flex-wrap gap-3">{children}</div> : null}
      </div>
    </section>
  );
}

export function BulletList({
  items,
  className,
}: {
  items: readonly string[];
  className?: string | undefined;
}) {
  return (
    <ul className={cn("grid gap-3", className)}>
      {items.map((item) => (
        <li key={item} className="flex gap-3 text-sm leading-relaxed md:text-base">
          <Check className="mt-1 size-4 shrink-0 text-brand" aria-hidden="true" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function CtaBand({
  title = "Get your panel price in writing",
  body = "Free on-site estimates across the Denver metro. We look at the equipment, then put one written price in front of you.",
}: {
  title?: string | undefined;
  body?: string | undefined;
}) {
  return (
    <Section tone="ink" size="compact">
      <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
        <div className="max-w-xl">
          <h2 className="text-2xl md:text-3xl">{title}</h2>
          <p className="mt-3 text-white/85">{body}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <CtaLink to="/request-estimate">
            Request a free estimate <ArrowRight className="size-4" aria-hidden="true" />
          </CtaLink>
          <CallButton tone="ghost-light" />
        </div>
      </div>
    </Section>
  );
}

/**
 * Compact, reusable financing prompt used anywhere price comes up.
 * Always routes to /financing so the Hearth application link stays in one place.
 */
export function FinancingCta({
  tone = "surface",
  className,
}: {
  tone?: "surface" | "ink" | "plain" | undefined;
  className?: string | undefined;
}) {
  const wrap =
    tone === "ink"
      ? "on-dark bg-ink text-ink-foreground"
      : tone === "plain"
        ? "border border-border bg-background"
        : "bg-surface";

  return (
    <div className={cn("p-6", wrap, className)}>
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
        <div className="min-w-0 max-w-xl">
          <h3 className="font-display text-base font-bold uppercase tracking-wide">
            Monthly payments available through {business.financingPartner}
          </h3>
          <p
            className={cn(
              "mt-2 text-sm leading-relaxed",
              tone === "ink" ? "text-white/85" : "text-muted-foreground",
            )}
          >
            Prequalifying takes about a minute and uses a soft credit check, so checking does not
            affect your credit score. Or{" "}
            <Link
              to="/financing"
              className={cn(
                "font-medium underline underline-offset-4",
                tone === "ink" ? "text-brand-foreground" : "text-brand",
              )}
            >
              read how financing works
            </Link>
            .
          </p>
        </div>
        <HearthBanner className="w-full shrink-0 lg:w-[380px] xl:w-[440px]" note={null} />
      </div>
      <p
        className={cn(
          "mt-3 text-xs leading-relaxed",
          tone === "ink" ? "text-white/75" : "text-muted-foreground",
        )}
      >
        Financing provided by third-party lenders through {business.financingPartner}, NMLS #
        {business.financingNmls}. Not a commitment to lend.
      </p>
    </div>
  );
}

export function Stars({ className }: { className?: string | undefined }) {
  return <StarsInner className={className} />;
}

/**
 * Reusable internal-linking block. Descriptive anchor text plus a one-line
 * reason to click, used by service, blog, resource and location pages.
 */
export function RelatedLinks({
  title,
  intro,
  links,
  columns = 3,
  className,
}: {
  title: string;
  intro?: string | undefined;
  links: { to: string; label: string; blurb?: string | undefined }[];
  columns?: 2 | 3 | undefined;
  className?: string | undefined;
}) {
  if (links.length === 0) return null;
  return (
    <div className={className}>
      <h2 className="text-2xl md:text-3xl">{title}</h2>
      {intro ? (
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">{intro}</p>
      ) : null}
      <div className={cn("mt-6 grid gap-4", columns === 2 ? "md:grid-cols-2" : "md:grid-cols-3")}>
        {links.map((l) => (
          <Link
            key={l.to + l.label}
            to={l.to as never}
            className="group flex flex-col border border-border bg-background p-5 transition-colors hover:border-brand"
          >
            <span className="font-display text-base font-bold group-hover:text-brand">
              {l.label}
            </span>
            {l.blurb ? (
              <span className="mt-2 text-sm leading-relaxed text-muted-foreground">{l.blurb}</span>
            ) : null}
            <span className="mt-4 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-brand">
              Read more <ArrowRight className="size-3.5" aria-hidden="true" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function StarsInner({ className }: { className?: string | undefined }) {
  return (
    <div
      className={cn("flex gap-0.5 text-brand", className)}
      role="img"
      aria-label="5 out of 5 stars"
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className="size-4 fill-current" aria-hidden="true" />
      ))}
    </div>
  );
}

export function ReviewCard({
  name,
  city,
  service,
  quote,
}: {
  name: string;
  city: string;
  service: string;
  quote: string;
}) {
  return (
    <figure className="flex h-full flex-col rounded-sm border border-border bg-background p-6">
      <Stars />
      <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground/90">
        "{quote}"
      </blockquote>
      <figcaption className="mt-5 border-t border-border pt-4 text-sm">
        <span className="font-display font-bold">{name}</span>
        <span className="text-muted-foreground"> — {city}</span>
        <div className="text-xs text-muted-foreground">{service}</div>
      </figcaption>
    </figure>
  );
}

function faqAnchorId(q: string) {
  return `faq-${q
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60)}`;
}

export function FaqList({
  items,
  anchors = false,
  openId,
}: {
  items: { q: string; a: string }[];
  anchors?: boolean;
  openId?: string | undefined;
}) {
  return (
    <div className="divide-y divide-border border-y border-border">
      {items.map((f) => {
        const id = faqAnchorId(f.q);
        return (
          <details
            key={f.q}
            id={anchors ? id : undefined}
            open={anchors && openId === id}
            className="group scroll-mt-28 py-5"
          >
            <summary className="flex cursor-pointer list-none items-start justify-between gap-4 font-display text-base font-bold md:text-lg">
              {f.q}
              <span
                className="mt-1 text-brand transition-transform group-open:rotate-45"
                aria-hidden="true"
              >
                +
              </span>
            </summary>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground md:text-base">
              {richText(f.a)}
            </p>
          </details>
        );
      })}
    </div>
  );
}

export { faqAnchorId };

export function Breadcrumbs({ trail }: { trail: { label: string; to?: string | undefined }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="border-b border-border bg-surface px-5 py-3">
      <ol className="mx-auto flex w-full max-w-6xl flex-wrap gap-2 text-xs text-muted-foreground">
        {trail.map((item, i) => (
          <li key={item.label} className="flex items-center gap-2">
            {item.to ? (
              <Link to={item.to as never} className="hover:text-brand">
                {item.label}
              </Link>
            ) : (
              <span className="text-foreground">{item.label}</span>
            )}
            {i < trail.length - 1 ? <span aria-hidden="true">/</span> : null}
          </li>
        ))}
      </ol>
    </nav>
  );
}
